import { Component, DesignerStyles, Layout, Panel, ResourceFileProps, TabProps } from "@mcleod/components";
import { Block, ScrollBehavior } from "@mcleod/core";
import { ResourceDesignerTab } from "@mcleod/designer";
import { PermissionsDesigner } from "./PermissionsDesigner";
import { PermissionsTabTooltip } from "./PermissionsTabTooltip";

export class PermissionsDesignerTab extends ResourceDesignerTab {
    designerPanel: Layout;
    designer: PermissionsDesigner;
    private _permedComponentMap: Map<string, Set<Component>> = new Map();
    private nestedLayoutComponentsMap: Map<string, Map<string, Set<Component>>> = new Map();
    private nestedLayoutMap: Map<string, Set<Layout>> = new Map();

    constructor(designer: PermissionsDesigner, props: ResourceFileProps, tabProps?: Partial<TabProps>) {
        super("layout", props, tabProps);
        this.designer = designer;
        this.designerPanel = new Layout({
            fillHeight: true,
            fillRow: true,
            _designer: designer,
            applyFieldLevelPermissions: false,
            applyFieldLevelLicensing: false
        });
        this.add(this.designerPanel);
    }

    _populateComponentMap() {
        this.trackAndStyleComponent(this.path, this.designerPanel);
        this.recurseIncludedComponents(this.path, this.designerPanel.discoverIncludedComponents(), this.trackAndStyleComponent.bind(this));
    }

    private recurseIncludedComponents(path: string, children: Component[], callback: (path: string, component: Component) => void) {
        if (children == null || path == null)
            return;
        children.forEach(child => {
            callback(path, child);
            if (child instanceof Layout && child.isNested === true) {
                this.addToNestedLayoutMap(child);

                this.recurseIncludedComponents(child.layoutName, child.discoverIncludedComponents(), callback);
            } else if (child instanceof Component) {
                this.recurseIncludedComponents(path, child.discoverIncludedComponents(), callback);
            }
        });
    }

    private addToNestedLayoutMap(child: Layout) {
        let layoutSet = this.nestedLayoutMap.get(child.layoutName);
        if (!layoutSet) {
            layoutSet = new Set();
            this.nestedLayoutMap.set(child.layoutName, layoutSet);
        }
        layoutSet.add(child);
    }

    private trackAndStyleComponent(path: string, component: Component) {
        if (path == null || (!this.isMainLayout(component) && component?.selectableInDesigner !== true))
            return;

        this.syncPermedComponentMap(component);

        if (path !== this.path) {
            let idMap = this.nestedLayoutComponentsMap.get(path);
            if (!idMap) {
                idMap = new Map();
                this.nestedLayoutComponentsMap.set(path, idMap);
            }

            let instances = idMap.get(component.id);
            if (!instances) {
                instances = new Set();
                idMap.set(component.id, instances);
            }
            instances.add(component);
        }
    }

    get nestedLayoutPaths(): string[] {
        return Array.from(this.nestedLayoutMap.keys());
    }

    get permedComponentMap(): Map<string, Set<Component>> {
        return this._permedComponentMap;
    }

    set permedComponentMap(value: Map<string, Set<Component>>) {
        this._permedComponentMap = value;
    }

    isMainLayout(component: Component): boolean {
        return component === this.designerPanel;
    }

    syncPermedComponentMap(component: Component, permed?: boolean) {
        const path = this.getComponentPath(component);
        if (permed == null) {
            if (this.isMainLayout(component)) {
                permed = this.designer.permissionsSource.hasTopLevelSecureItemRows(path);
            } else {
                permed = this.designer.permissionsSource
                    .getSecureItemRowsForPage(this.getComponentPath(component))
                    .some(row => row.get("item_name") === component.id);
            }
        }
        permed = permed || this.designer.isNestedLayoutWithGlobalPermissions(component);

        let components = this.permedComponentMap.get(path);
        if (permed) {
            if (!components) {
                components = new Set();
                this.permedComponentMap.set(path, components);
            }
            components.add(component);
        } else if (components) {
            components.delete(component);
            if (components.size === 0) {
                this.permedComponentMap.delete(path);
            }
        }
        this.syncPermedStyles(component, permed);

        this.headingLabel.color = this.permedComponentMap.size > 0 ? "error" : "primary";
    }

    syncPermedStyles(component: Component, permed = this.isPermedComponent(component)) {
        component.setClassIncluded(DesignerStyles.permed, permed);
        if (this.designer.firstSelectedLayoutComponent === component) {
            component.setClassIncluded(DesignerStyles.permedSelected, permed);
            component.setClassIncluded(DesignerStyles.designerSelected, !permed);
        }
    }

    isPermedComponent(component: Component): boolean {
        const componentPath = this.getComponentPath(component);
        return this.permedComponentMap.get(componentPath)?.has(component) ?? false;
    }

    private getComponentPath(component: Component): string {
        return component.owner?.layoutName ?? this.path;
    }

    permsChanged(component: Component, deleted: boolean) {
        const permed = !deleted;
        this.syncPermedComponentMap(component, permed);
        const sourcePath = this.path;
        const sourceIsMainLayout = this.isMainLayout(component);
        const componentId = component.id;

        // sync up tabs that have this same component in a nested layout
        this.designer.tabset.tabs.forEach((tab: PermissionsDesignerTab) => {
            if (tab !== this) {
                tab.handlePermsChangeFromOtherTab(sourceIsMainLayout, sourcePath, componentId, permed);
            }
        });
    }

    private handlePermsChangeFromOtherTab(
        sourceIsMainLayout: boolean,
        sourcePath: string,
        componentId: string,
        permed: boolean,
    ): void {
        if (sourceIsMainLayout) {
            this.nestedLayoutMap.get(sourcePath)?.
                forEach(nestedLayout => this.syncPermedComponentMap(nestedLayout));
        } else {
            this.nestedLayoutComponentsMap.get(sourcePath)?.get(componentId)?.
                forEach(comp => this.syncPermedComponentMap(comp, permed));
        }
    }

    getSearchRequestEndpointAndBody(): [string, any] {
        const filter: any = {
            ...this.getSearchRequestBody(),
            apply_field_level_perms: false,
            apply_field_level_licensing: false,
            apply_field_level_company_type: false,
            apply_field_level_ltl_type: false,
            apply_field_level_experiment: false,
            apply_field_level_multi_currency: false
        };
        return [this.designer.endpointPath, filter];
    }

    selectAndScrollToComponent(layoutPath: string, component: Component) {
        if (component != null) {
            component.scrollIntoView({ behavior: ScrollBehavior.INSTANT, block: Block.CENTER });
            this.designer.selectComponent(this.path === layoutPath ? component : null);
        }
    }

    override async createTooltipPanel(): Promise<Panel> {
        if (this.permedComponentMap.size === 0)
            return null;
        return new PermissionsTabTooltip(this);
    }
}
